/* =========================================================================
   New cart drawer (A/B redesign) behaviour:
     - Cart / Wishlist tab switching
     - Live wishlist count
     - Wishlist tab grid (reuses the product-card view)
     - Recently viewed strip
     - In-drawer quick-buy slide-up (choose variant/qty, add to cart, refresh)

   The cart drawer markup is re-rendered wholesale by the cart JS on every update,
   so everything here is bound with event delegation on `document` and re-hydrated
   via a MutationObserver rather than by binding to elements directly.
   ========================================================================= */

import { getLocalWishlist } from './novicell_wishlist-utils.js';
import { adaptIconToImage, scheduleIdle } from './novicell_icon-contrast.js';
import { formatMoney } from './novicell_shopify-utility.js';

const RECENTLY_VIEWED_KEY = 'recentlyViewed';
const RECENTLY_VIEWED_SHOW = 3;
const WISHLIST_LIMIT = 12;

/* ---------- small helpers ---------- */

const productJsCache = new Map();

async function fetchProductJson(handle) {
  if (productJsCache.has(handle)) return productJsCache.get(handle);
  const promise = fetch(`/products/${handle}.js`)
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);
  productJsCache.set(handle, promise);
  return promise;
}

const swatchMapCache = new Map();

// Map of option value -> swatch image url, read from the product page so the
// quick-buy shows exactly the same swatches as the PDP variant picker.
async function fetchSwatchMap(handle) {
  if (swatchMapCache.has(handle)) return swatchMapCache.get(handle);
  const promise = fetch(`/products/${handle}`)
    .then((res) => (res.ok ? res.text() : ''))
    .then((text) => {
      const map = {};
      // Read the swatch labels straight from the product page so the quick-buy uses
      // exactly the same swatch images as the PDP variant picker.
      const doc = new DOMParser().parseFromString(text, 'text/html');
      doc.querySelectorAll('label.variant__color-swatch-label[style*="background-image"]').forEach((label) => {
        const value = label.getAttribute('data-tooltip');
        const match = (label.getAttribute('style') || '').match(/url\((['"]?)([^'")]+)['"]?\)/);
        if (value && match && match[2]) map[value] = match[2];
      });
      return map;
    })
    .catch(() => ({}));
  swatchMapCache.set(handle, promise);
  return promise;
}

async function fetchProductCardHtml(handle) {
  try {
    const res = await fetch(`/products/${handle}?view=product-card`);
    if (!res.ok) return null;
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const card = doc.querySelector('.card-wrapper');
    return card ? card.outerHTML : null;
  } catch (err) {
    console.error('[cart-new] product card fetch failed', handle, err);
    return null;
  }
}

function getRecentlyViewed() {
  try {
    return JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY)) || [];
  } catch {
    return [];
  }
}

// Handles of products currently in the cart, read straight from the rendered
// cart items so it stays in sync with the drawer (which the cart JS re-renders
// wholesale on every change). Used to keep "recently viewed" from suggesting
// something the shopper has already added.
function getCartHandles(root) {
  const scope = root || document;
  const handles = new Set();
  scope.querySelectorAll('#CartDrawer-CartItems a[href*="/products/"]').forEach((a) => {
    const match = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    if (match) handles.add(match[1]);
  });
  return handles;
}

function getStrings(root) {
  const qb = getQuickBuy(root);
  const d = (qb && qb.dataset) || {};
  return {
    add: d.strAdd || 'Add to cart',
    details: d.strDetails || 'See full product details',
    qty: d.strQty || 'Quantity',
    loading: d.strLoading || 'Loading…',
    error: d.strError || 'Something went wrong. Please try again.',
    quickbuy: d.strQuickbuy || 'Quick buy',
  };
}

/* ---------- tab switching ---------- */

function activateTab(root, tabName) {
  if (!root) return;
  root.querySelectorAll('[data-cart-tab-trigger]').forEach((trigger) => {
    const isActive = trigger.dataset.cartTabTrigger === tabName;
    trigger.classList.toggle('is-active', isActive);
    trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
  root.querySelectorAll('[data-cart-tab-panel]').forEach((panel) => {
    panel.toggleAttribute('hidden', panel.dataset.cartTabPanel !== tabName);
  });

  if (tabName === 'wishlist') renderWishlistGrid(root);
}

/* ---------- wishlist count + grid ---------- */

function updateWishlistCount(root) {
  const scope = root || document;
  const count = String(getLocalWishlist().length);
  scope.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    // Only write when it actually changes, otherwise the MutationObserver that
    // calls hydrate() would re-trigger itself in a loop.
    if (el.textContent !== count) el.textContent = count;
  });
}

async function renderWishlistGrid(root) {
  const grid = (root || document).querySelector('[data-cart-wishlist-grid]');
  if (!grid) return;
  const emptyEl = (root || document).querySelector('[data-cart-wishlist-empty]');
  const handles = getLocalWishlist().slice(0, WISHLIST_LIMIT);

  if (grid.dataset.renderedFor === handles.join(',')) return; // avoid redundant work
  grid.dataset.renderedFor = handles.join(',');

  if (!handles.length) {
    grid.innerHTML = '';
    if (emptyEl) emptyEl.hidden = false;
    return;
  }
  if (emptyEl) emptyEl.hidden = true;

  const strings = getStrings(root);
  const cards = await Promise.all(handles.map(fetchProductCardHtml));
  grid.innerHTML = '';
  cards.forEach((card, i) => {
    if (!card) return;
    const li = document.createElement('li');
    li.className = 'column';
    li.innerHTML = card;
    // Place the quick-buy "+" over the product image rather than below the card.
    const mediaTarget = li.querySelector('.card__media') || li.querySelector('.card__inner') || li;
    mediaTarget.insertAdjacentHTML('beforeend', quickAddTriggerHtml(handles[i], strings.quickbuy));
    // Flip the "+" light/dark to stay legible on the image behind it.
    adaptQuickAddTrigger(
      mediaTarget.querySelector('.cart-quick-add-trigger'),
      li.querySelector('.card__media img, .media img, img'),
    );
    grid.appendChild(li);
  });
}

/* ---------- recently viewed ---------- */

function quickAddTriggerHtml(handle, label) {
  return (
    `<button type="button" class="cart-quick-add-trigger" data-cart-quick-add data-handle="${handle}" ` +
    `aria-label="${label || 'Quick buy'}">` +
    '<svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true" focusable="false">' +
    '<path d="M7 0v14M0 7h14" stroke="currentColor" stroke-width="1.4"/></svg>' +
    '</button>'
  );
}

/**
 * The quick-add "+" sits in the bottom-right corner of a product image, so it
 * needs the same light/dark adaptation the wishlist heart uses — sample the
 * pixels behind it and flip to dark on light imagery. Deferred to idle time so
 * the canvas read never competes with rendering the drawer.
 */
function adaptQuickAddTrigger(trigger, sourceImg) {
  if (!trigger || !sourceImg) return;
  scheduleIdle(() =>
    adaptIconToImage(trigger, sourceImg, {
      corner: 'bottom-right',
      onLightClass: 'cart-quick-add-trigger--on-light',
    }),
  );
}

async function renderRecentlyViewed(root) {
  const wrapper = (root || document).querySelector('[data-cart-recently]');
  if (!wrapper) return;
  const list = wrapper.querySelector('[data-cart-recently-list]');
  if (!list) return;

  // Only fetch as many candidates as we might need: even if the most-recent
  // items are all already in the cart, (cart size + 3) guarantees 3 left over
  // to show. We then drop the in-cart ones and keep the first 3.
  const cartHandles = getCartHandles(root);
  const candidates = getRecentlyViewed().slice(0, cartHandles.size + RECENTLY_VIEWED_SHOW);

  // Re-render when either the candidate list or the cart contents change.
  const renderKey = `${candidates.join(',')}#${[...cartHandles].sort().join(',')}`;
  if (list.dataset.renderedFor === renderKey) return;
  list.dataset.renderedFor = renderKey;

  if (!candidates.length) {
    wrapper.hidden = true;
    return;
  }

  const products = (await Promise.all(candidates.map(fetchProductJson)))
    .filter(Boolean)
    .filter((product) => !cartHandles.has(product.handle))
    .slice(0, RECENTLY_VIEWED_SHOW);
  if (!products.length) {
    wrapper.hidden = true;
    return;
  }

  const strings = getStrings(root);
  list.innerHTML = products
    .map((product) => {
      const img = product.featured_image
        ? `<img src="${product.featured_image}" alt="${(product.title || '').replace(/"/g, '&quot;')}" loading="lazy">`
        : '';
      return (
        `<li class="cart-recently__item">` +
        `<a class="cart-recently__thumb" href="${product.url}" tabindex="-1" aria-hidden="true">${img}</a>` +
        quickAddTriggerHtml(product.handle, strings.quickbuy) +
        `</li>`
      );
    })
    .join('');
  wrapper.hidden = false;

  // Flip each "+" light/dark to stay legible on the thumbnail behind it.
  list.querySelectorAll('.cart-recently__item').forEach((item) => {
    adaptQuickAddTrigger(
      item.querySelector('.cart-quick-add-trigger'),
      item.querySelector('.cart-recently__thumb img'),
    );
  });
}

/* ---------- quick-buy slide-up ---------- */

function getQuickBuy(root) {
  return (root || document).querySelector('[data-cart-quick-buy]');
}

function closeQuickBuy(root) {
  const qb = getQuickBuy(root);
  if (!qb) return;
  qb.classList.remove('is-open');
  const onEnd = () => {
    qb.hidden = true;
    qb.removeEventListener('transitionend', onEnd);
  };
  qb.addEventListener('transitionend', onEnd);
  // Fallback if no transition fires
  setTimeout(() => {
    if (!qb.classList.contains('is-open')) qb.hidden = true;
  }, 400);
}

function resolveVariant(product, selected) {
  return product.variants.find((v) => selected.every((value, i) => v.options[i] === value));
}

function optionLabel(option) {
  // /products/{handle}.js returns options as objects ({ name, position, values })
  // on newer stores and as plain strings on older ones.
  return typeof option === 'string' ? option : (option && option.name) || '';
}

function renderQuickBuyOptions(product, selected, swatchMap) {
  // A single variant means there is nothing to choose.
  if (product.variants.length === 1) {
    return '';
  }

  const swatches = swatchMap || {};

  return product.options
    .map((optionName, optIndex) => {
      // Values in display order.
      const values = [];
      product.variants.forEach((v) => {
        const val = v.options[optIndex];
        if (!values.includes(val)) values.push(val);
      });

      // Swatch image per value, from the same source the product page uses.
      const valueImages = {};
      let hasImages = false;
      values.forEach((val) => {
        const img = swatches[val];
        if (img) {
          valueImages[val] = img;
          hasImages = true;
        }
      });

      const buttons = values
        .map((val) => {
          // Availability: is there an available variant with this value + current other selections?
          const probe = selected.slice();
          probe[optIndex] = val;
          const candidate = product.variants.find(
            (v) => probe.every((s, i) => s == null || i === optIndex || v.options[i] === s) && v.options[optIndex] === val,
          );
          const available = candidate ? candidate.available : false;
          const isSelected = selected[optIndex] === val;
          const inner =
            hasImages && valueImages[val]
              ? `<img src="${valueImages[val]}" alt="${val}">`
              : val;
          // Cross out unavailable values with the same diagonal line the PDP
          // swatches use (sized in JS once laid out — see sizeQuickBuyDiagonals).
          const cross = available ? '' : '<span class="diagonal" aria-hidden="true"></span>';
          return (
            `<button type="button" class="cart-quick-buy__value${isSelected ? ' is-selected' : ''}" ` +
            `data-option-index="${optIndex}" data-option-value="${String(val).replace(/"/g, '&quot;')}"` +
            `${available ? '' : ' disabled'}>${cross}${inner}</button>`
          );
        })
        .join('');

      return (
        `<div class="cart-quick-buy__option">` +
        `<span class="cart-quick-buy__option-label">${optionLabel(optionName)}: <em>${selected[optIndex] || ''}</em></span>` +
        `<div class="cart-quick-buy__values${hasImages ? ' cart-quick-buy__values--swatch' : ''}">${buttons}</div>` +
        `</div>`
      );
    })
    .join('');
}

// Draw the diagonal "unavailable" line across each sold-out value. The width
// depends on the rendered box, so it can only be computed once laid out —
// mirrors handleDiagonalLineOOSButtons in novicell_main-product.js.
function sizeQuickBuyDiagonals(qb) {
  requestAnimationFrame(() => {
    qb.querySelectorAll('.cart-quick-buy__value:disabled').forEach((button) => {
      const line = button.querySelector('.diagonal');
      if (!line) return;
      const width = button.offsetWidth;
      const height = button.offsetHeight;
      if (!width || !height) return;
      const angleDeg = Math.atan(height / width) * (180 / Math.PI);
      line.style.width = `${Math.sqrt(width * width + height * height)}px`;
      line.style.height = '2px';
      line.style.transform = `rotate(${angleDeg - 1}deg)`;
    });
  });
}

function renderQuickBuyBody(qb, product, swatchMap) {
  const content = qb.querySelector('[data-quick-buy-content]');
  const titleEl = qb.querySelector('[data-quick-buy-title]');
  if (titleEl) titleEl.textContent = product.title;

  const selected = product.options.map((_, i) => product.variants[0].options[i]);
  qb._quickBuyState = { product, selected, swatchMap: swatchMap || {} };

  const strings = getStrings(qb);
  const addLabel = strings.add;
  const detailsLabel = strings.details;

  content.innerHTML =
    renderQuickBuyOptions(product, selected, swatchMap) +
    `<div class="cart-quick-buy__qty">` +
    `<label class="cart-quick-buy__option-label" for="CartQuickBuy-qty">${strings.qty}</label>` +
    `<quantity-input class="quantity"><input id="CartQuickBuy-qty" class="quantity__input" type="number" min="1" value="1" name="quantity"></quantity-input>` +
    `</div>` +
    `<p class="cart-quick-buy__error" data-quick-buy-error hidden></p>` +
    `<div class="cart-quick-buy__actions">` +
    `<button type="button" class="button cart-quick-buy__submit" data-quick-buy-submit>` +
    `<span class="cart-quick-buy__submit-inner">` +
    `<span class="cart-quick-buy__submit-label">${addLabel}</span>` +
    `<span class="price cart-quick-buy__price" data-quick-buy-price></span>` +
    `</span>` +
    `</button>` +
    `<a class="cart-quick-buy__details-link" href="${product.url}">${detailsLabel}</a>` +
    `</div>`;

  syncQuickBuyState(qb);
}

function syncQuickBuyState(qb) {
  const state = qb._quickBuyState;
  if (!state) return;
  const { product, selected, swatchMap } = state;
  const variant = resolveVariant(product, selected);
  const submit = qb.querySelector('[data-quick-buy-submit]');
  if (submit) {
    submit.disabled = !variant || !variant.available;
    submit.dataset.variantId = variant ? variant.id : '';
  }
  // Variants can differ in price, so keep the shown price in sync with the
  // current selection.
  const priceEl = qb.querySelector('[data-quick-buy-price]');
  if (priceEl) {
    priceEl.textContent = variant ? formatMoney(variant.price, window.shopMoneyFormat) : '';
  }
  // Re-render option availability states.
  const content = qb.querySelector('[data-quick-buy-content]');
  const optionsHtml = renderQuickBuyOptions(product, selected, swatchMap);
  const optionsWrap = document.createElement('div');
  optionsWrap.innerHTML = optionsHtml;
  const existingOptions = content.querySelectorAll('.cart-quick-buy__option');
  const newOptions = optionsWrap.querySelectorAll('.cart-quick-buy__option');
  existingOptions.forEach((el, i) => {
    if (newOptions[i]) el.replaceWith(newOptions[i]);
  });

  sizeQuickBuyDiagonals(qb);
}

async function openQuickBuy(handle, root) {
  const qb = getQuickBuy(root);
  if (!qb) return;
  const strings = getStrings(qb);
  const content = qb.querySelector('[data-quick-buy-content]');
  content.innerHTML = `<div class="cart-quick-buy__loading">${strings.loading}</div>`;
  qb.hidden = false;
  // force reflow so the transition runs
  void qb.offsetWidth;
  qb.classList.add('is-open');

  const [product, swatchMap] = await Promise.all([fetchProductJson(handle), fetchSwatchMap(handle)]);
  if (!product) {
    content.innerHTML = `<p class="cart-quick-buy__error">${strings.error}</p>`;
    return;
  }
  renderQuickBuyBody(qb, product, swatchMap);
  const panel = qb.querySelector('.cart-quick-buy__panel');
  if (panel) panel.focus();
}

async function quickBuyAddToCart(qb) {
  const submit = qb.querySelector('[data-quick-buy-submit]');
  const errorEl = qb.querySelector('[data-quick-buy-error]');
  const qtyInput = qb.querySelector('#CartQuickBuy-qty');
  const variantId = submit && submit.dataset.variantId;
  if (!variantId) return;

  const cart = document.querySelector('cart-drawer');
  if (!cart) return;

  submit.classList.add('loading');
  submit.disabled = true;
  if (errorEl) errorEl.hidden = true;

  const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', qtyInput ? qtyInput.value || 1 : 1);
  formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
  formData.append('sections_url', window.location.pathname);
  config.body = formData;

  try {
    const response = await fetch(`${window.routes.cart_add_url}`, config).then((r) => r.json());
    if (response.status) {
      if (errorEl) {
        errorEl.textContent = response.description || response.message;
        errorEl.hidden = false;
      }
      submit.classList.remove('loading');
      submit.disabled = false;
      return;
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-quick-buy' });
    }
    cart.classList.remove('is-empty');
    cart.renderContents(response); // re-renders #CartDrawer (closes the slide-up)
  } catch (err) {
    console.error('[cart-new] quick-buy add failed', err);
    if (errorEl) {
      errorEl.textContent = getStrings(qb).error;
      errorEl.hidden = false;
    }
    submit.classList.remove('loading');
    submit.disabled = false;
  }
}

/* ---------- hydration ---------- */

function hydrate(root) {
  updateWishlistCount(root);
  renderRecentlyViewed(root);
  // Only build the wishlist grid when its tab is the active one.
  const activePanel = (root || document).querySelector('[data-cart-tab-panel="wishlist"]:not([hidden])');
  if (activePanel) renderWishlistGrid(root);
}

/* ---------- delegated events (bound once) ---------- */

function initDelegation() {
  document.addEventListener('click', (event) => {
    const tabTrigger = event.target.closest('[data-cart-tab-trigger]');
    if (tabTrigger) {
      activateTab(tabTrigger.closest('.drawer__inner') || document, tabTrigger.dataset.cartTabTrigger);
      return;
    }

    const quickAdd = event.target.closest('[data-cart-quick-add]');
    if (quickAdd && quickAdd.closest('cart-drawer')) {
      event.preventDefault();
      openQuickBuy(quickAdd.dataset.handle, quickAdd.closest('cart-drawer'));
      return;
    }

    // Intercept the product-card quick-add inside the wishlist grid → use slide-up.
    const cardQuickAdd = event.target.closest('.wishlist__grid .quick-add__submit, [data-cart-wishlist-grid] .quick-add__submit');
    if (cardQuickAdd) {
      const card = cardQuickAdd.closest('.card-wrapper');
      const heartBtn = card && card.querySelector('[data-product-handle]');
      const handle = heartBtn && heartBtn.dataset.productHandle;
      if (handle) {
        event.preventDefault();
        event.stopPropagation();
        openQuickBuy(handle, cardQuickAdd.closest('cart-drawer'));
        return;
      }
    }

    const qbOverlay = event.target.closest('[data-quick-buy-overlay]');
    const qbClose = event.target.closest('[data-quick-buy-close]');
    if (qbOverlay || qbClose) {
      closeQuickBuy(event.target.closest('cart-drawer'));
      return;
    }

    const value = event.target.closest('.cart-quick-buy__value');
    if (value && !value.disabled) {
      const qb = value.closest('[data-cart-quick-buy]');
      const state = qb && qb._quickBuyState;
      if (state) {
        state.selected[Number(value.dataset.optionIndex)] = value.dataset.optionValue;
        syncQuickBuyState(qb);
      }
      return;
    }

    const qbSubmit = event.target.closest('[data-quick-buy-submit]');
    if (qbSubmit && !qbSubmit.disabled) {
      quickBuyAddToCart(qbSubmit.closest('[data-cart-quick-buy]'));
    }
  });

  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') {
      const qb = document.querySelector('[data-cart-quick-buy].is-open');
      if (qb) closeQuickBuy(qb.closest('cart-drawer'));
    }
  });

  // Keep count + grid fresh when the wishlist changes anywhere.
  ['wishlist:updated', 'wishlist:removed', 'wishlist:render-content'].forEach((evt) => {
    document.addEventListener(evt, () => {
      const root = document.querySelector('.cart-drawer--new .drawer__inner');
      updateWishlistCount(root);
      const grid = document.querySelector('[data-cart-wishlist-grid]');
      if (grid) grid.dataset.renderedFor = ''; // force re-render
      const activePanel = document.querySelector('[data-cart-tab-panel="wishlist"]:not([hidden])');
      if (activePanel) renderWishlistGrid(root);
    });
  });
  window.addEventListener('storage', (e) => {
    if (e.key === 'wishlist') updateWishlistCount();
  });
}

function initObserver() {
  const drawer = document.querySelector('cart-drawer.cart-drawer--new');
  if (!drawer) return;
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      hydrate(drawer.querySelector('.drawer__inner'));
    });
  });
  observer.observe(drawer, { childList: true, subtree: true });
  hydrate(drawer.querySelector('.drawer__inner'));
}

/**
 * Keep in-drawer UI state in sync with the drawer opening/closing. The tab and
 * quick-buy state live in the DOM, which survives a plain close/open (the cart
 * JS only re-renders on cart changes), so without this a shopper would reopen
 * the drawer on the Wishlist tab, or with the quick-buy slide-up still showing.
 */
function initDrawerStateSync() {
  const drawer = document.querySelector('cart-drawer.cart-drawer--new');
  if (!drawer) return;
  const observer = new MutationObserver(() => {
    if (drawer.getAttribute('aria-hidden') === 'false') {
      // Opened — always start on the Cart tab.
      const active = drawer.querySelector('[data-cart-tab-trigger].is-active');
      if (active && active.dataset.cartTabTrigger !== 'cart') {
        activateTab(drawer.querySelector('.drawer__inner'), 'cart');
      }
    } else {
      // Closed — dismiss the quick-buy slide-up so it isn't still open next time.
      closeQuickBuy(drawer);
    }
  });
  observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
}

function init() {
  if (window.__cartNewInit) return;
  window.__cartNewInit = true;
  initDelegation();
  initObserver();
  initDrawerStateSync();

  // Re-fit the sold-out diagonal lines if the viewport changes while the
  // quick-buy is open (their width is derived from the rendered box).
  window.addEventListener('resize', () => {
    const qb = document.querySelector('[data-cart-quick-buy].is-open');
    if (qb) sizeQuickBuyDiagonals(qb);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
